/**
 * Shared pet profile photo upload (portal + wizard-2).
 */
(function (global) {
  'use strict';

  var MAX_BYTES = 15 * 1024 * 1024;
  var ENDPOINT = '/api/portal/pet-profile-photo';

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function toastKey(key) {
    if (global.bzToast && typeof global.bzToast.showKey === 'function') {
      global.bzToast.showKey(key);
      return true;
    }
    return false;
  }

  function toastError(message) {
    if (global.bzToast && typeof global.bzToast.show === 'function') {
      global.bzToast.show({ type: 'error', message: message });
      return true;
    }
    return false;
  }

  function setLoading(wrap, loading) {
    if (!wrap) return;
    wrap.classList.toggle('bz-pet-photo--loading', !!loading);
    wrap.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  function notifyPhotoUpdate(options, payload) {
    if (typeof options.onPhotoUpdate === 'function') {
      options.onPhotoUpdate(payload);
    } else if (typeof options.onSuccess === 'function') {
      options.onSuccess(payload);
    }
  }

  function uploadPetProfilePhoto(options) {
    var backendUrl = String(options.backendUrl || '').replace(/\/$/, '');
    var file = options.file;
    var petMetaobjectId = options.petMetaobjectId;
    var petName = options.petName || '';

    if (!backendUrl) return Promise.reject(new Error('Backend not configured'));
    if (!file) return Promise.reject(new Error('No file selected'));
    if (!petMetaobjectId) {
      return Promise.reject(new Error('Pet profile not loaded'));
    }
    if (file.size > MAX_BYTES) {
      return Promise.reject(new Error('too_large'));
    }

    var formData = new FormData();
    formData.append('file', file);
    formData.append('petMetaobjectId', petMetaobjectId);
    formData.append('petName', petName);

    return fetch(backendUrl + ENDPOINT, {
      method: 'POST',
      body: formData,
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok || !body || body.ok === false) {
          throw new Error((body && body.message) || 'upload_failed');
        }
        return body;
      });
    });
  }

  function renderPortalPhoto(wrap, pet, t) {
    if (!wrap) return;
    var photoUrl = pet && pet.photoUrl ? pet.photoUrl : '';
    var petName = (pet && pet.name) || '';
    if (photoUrl) {
      wrap.innerHTML =
        '<img src="' +
        escapeHtml(photoUrl) +
        '" alt="' +
        escapeHtml(petName) +
        '" class="dogs-profile__photo-img" loading="lazy">';
      wrap.classList.remove('dogs-profile__photo--placeholder');
    } else {
      wrap.innerHTML =
        '<span class="dogs-profile__photo-placeholder-text">' +
        escapeHtml((t && t('add_photo')) || 'Add photo') +
        '</span>';
      wrap.classList.add('dogs-profile__photo--placeholder');
    }
  }

  function renderWizardPhoto(wrap, photoUrl, petName) {
    if (!wrap) return;
    var placeholderIcon =
      '<svg class="profile-name-photo__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" stroke="currentColor" stroke-width="1.5"/>' +
      '<path d="M4 19.5V17a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '</svg>';
    if (photoUrl) {
      wrap.innerHTML =
        '<img src="' +
        escapeHtml(photoUrl) +
        '" alt="' +
        escapeHtml(petName || '') +
        '" class="profile-name-photo__img" loading="lazy">';
      wrap.classList.remove('profile-name-photo__surface--placeholder');
    } else {
      wrap.innerHTML = placeholderIcon;
      wrap.classList.add('profile-name-photo__surface--placeholder');
    }
  }

  function bindPhotoUpload(options) {
    var wrap = options.wrap;
    var input = options.input;
    if (!wrap || !input) return function () {};

    wrap.classList.add('bz-pet-photo--clickable');
    if (!wrap.hasAttribute('tabindex')) wrap.setAttribute('tabindex', '0');
    if (!wrap.getAttribute('role')) wrap.setAttribute('role', 'button');

    function openPicker() {
      if (wrap.classList.contains('bz-pet-photo--loading')) return;
      input.click();
    }

    function onKeydown(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openPicker();
      }
    }

    function onChange() {
      var file = input.files && input.files[0];
      input.value = '';
      if (!file) return;

      var petMetaobjectId = options.getPetMetaobjectId();
      var petName = options.getPetName ? options.getPetName() : '';
      var previousPhotoUrl = options.getCurrentPhotoUrl ? options.getCurrentPhotoUrl() : '';
      var localObjectUrl = null;

      if (!petMetaobjectId) {
        if (!toastKey('photo_upload_unavailable')) {
          toastError('Unable to upload photo right now');
        }
        return;
      }

      if (file.size > MAX_BYTES) {
        toastKey('photo_too_large');
        return;
      }

      try {
        localObjectUrl = URL.createObjectURL(file);
        notifyPhotoUpdate(options, { photoUrl: localObjectUrl, isLocal: true });
      } catch (e) {
        localObjectUrl = null;
      }

      setLoading(wrap, true);

      uploadPetProfilePhoto({
        backendUrl: options.backendUrl,
        file: file,
        petMetaobjectId: petMetaobjectId,
        petName: petName,
      })
        .then(function (result) {
          var cdnUrl = result && result.photoUrl ? String(result.photoUrl) : '';

          if (cdnUrl) {
            if (localObjectUrl) {
              URL.revokeObjectURL(localObjectUrl);
              localObjectUrl = null;
            }
            notifyPhotoUpdate(options, { photoUrl: cdnUrl, isLocal: false });
          } else if (!localObjectUrl) {
            throw new Error('upload_failed');
          }

          toastKey('photo_saved');
        })
        .catch(function (err) {
          if (localObjectUrl) {
            URL.revokeObjectURL(localObjectUrl);
            localObjectUrl = null;
          }
          notifyPhotoUpdate(options, { photoUrl: previousPhotoUrl, isLocal: false });

          var code = err && err.message;
          if (code === 'too_large') toastKey('photo_too_large');
          else if (code === 'Pet profile not loaded') toastKey('photo_profile_not_loaded');
          else if (!toastKey('photo_upload_failed')) toastError(code || 'Upload failed');

          if (typeof options.onError === 'function') options.onError(err);
        })
        .finally(function () {
          setLoading(wrap, false);
        });
    }

    wrap.addEventListener('click', openPicker);
    wrap.addEventListener('keydown', onKeydown);
    input.addEventListener('change', onChange);

    return function unbind() {
      wrap.removeEventListener('click', openPicker);
      wrap.removeEventListener('keydown', onKeydown);
      input.removeEventListener('change', onChange);
    };
  }

  global.bzPetPhotoUpload = {
    MAX_BYTES: MAX_BYTES,
    upload: uploadPetProfilePhoto,
    bind: bindPhotoUpload,
    renderPortalPhoto: renderPortalPhoto,
    renderWizardPhoto: renderWizardPhoto,
  };
})(typeof window !== 'undefined' ? window : this);
